import React from 'react';
import { Avatar, useTheme } from '@mui/material';
import { 
  Person,
  BusinessCenter,
  School,
  LocalAtm,
  Gavel,
  DirectionsCar,
  HomeWork,
  Flight,
  Train,
  Sailing,
  RocketLaunch,
  Diamond,
  Castle,
  Pets,
  SmartToy,
  EmojiEvents,
  Psychology
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { rampByName } from '../common/styles/monochromeRamp';

interface UserAvatarMUIProps {
  size?: 'small' | 'medium' | 'large';
  sx?: any;
}

// Avatar icon mapping
const AVATAR_ICONS: Record<string, any> = {
  business: BusinessCenter,
  scholar: School,
  banker: LocalAtm,
  lawyer: Gavel,
  car: DirectionsCar,
  property: HomeWork,
  flight: Flight,
  train: Train,
  sailing: Sailing,
  rocket: RocketLaunch,
  diamond: Diamond,
  castle: Castle,
  pet: Pets,
  robot: SmartToy,
  trophy: EmojiEvents,
  brain: Psychology
};

// Avatar colour was sixteen Material hues, none of which said anything about
// the person (CLAUDE.md invariant 4; 2026-09-07 quality report). A neutral
// shade, deterministic on the chosen icon so it does not change on a reload.

const UserAvatarMUI: React.FC<UserAvatarMUIProps> = ({ size = 'medium', sx = {} }) => {
  const theme = useTheme();
  const { user, userAccount } = useAuth();

  const sizeMap = {
    small: { width: 32, height: 32, fontSize: 20 },
    medium: { width: 40, height: 40, fontSize: 24 },
    large: { width: 120, height: 120, fontSize: 60 }
  };

  const getInitials = () => {
    if (userAccount?.name) {
      return userAccount.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  // If user has a custom avatar URL, use it
  if (userAccount?.avatarUrl) {
    return (
      <Avatar
        src={userAccount.avatarUrl}
        alt={userAccount.name || user?.displayName || 'User avatar'}
        sx={{
          ...sizeMap[size],
          ...sx
        }}
      />
    );
  }

  // Check if user has selected an avatar icon
  const avatarIcon = (userAccount as any)?.avatarIcon;
  if (avatarIcon && AVATAR_ICONS[avatarIcon]) {
    const IconComponent = AVATAR_ICONS[avatarIcon];
    return (
      <Avatar
        sx={{
          ...sizeMap[size],
          bgcolor: rampByName(theme, avatarIcon || 'default'),
          '& .MuiSvgIcon-root': {
            fontSize: sizeMap[size].fontSize
          },
          ...sx
        }}
      >
        {React.createElement(IconComponent)}
      </Avatar>
    );
  }

  // Otherwise show default icon or initials
  const showIcon = !userAccount?.name && !user?.displayName;

  return (
    <Avatar
      sx={{
        ...sizeMap[size],
        bgcolor: 'primary.main',
        ...sx
      }}
    >
      {showIcon ? (
        <Person sx={{ fontSize: sizeMap[size].fontSize }} />
      ) : (
        getInitials()
      )}
    </Avatar>
  );
};

export default UserAvatarMUI;